/**
 * `showOnKioskAndWebApp` is an LH flag that can appear at the building, floor,
 * or room level. This helper checks one node; consumers combine ancestor checks
 * where needed, such as Navigator's building -> floor -> room access checks.
 *
 * Only an EXPLICIT `false` is actionable (it hides that entity). Anything else
 * — missing / null / undefined / non-boolean / explicit `true` — is treated as
 * "not hidden", so behavior is exactly today's unless the flag is explicitly
 * set to `false`.
 */
export const isHiddenOnKiosk = (node) => {
    if (node && typeof node === 'object') {
        return node.showOnKioskAndWebApp === false;
    }
    return false;
};
/** Find the LH floor node for a floor number (undefined if not resolvable). */
const findFloorNode = (location, floorNumber) => {
    if (floorNumber === null ||
        floorNumber === undefined ||
        !Number.isFinite(floorNumber) ||
        !location ||
        typeof location !== 'object') {
        return undefined;
    }
    const children = location.children;
    if (!Array.isArray(children))
        return undefined;
    return children.find((child) => child &&
        typeof child === 'object' &&
        Number(child.floorNumber) ===
            Number(floorNumber));
};
/**
 * Build a fast per-floor predicate that reports whether a room/desk is allowed
 * to be highlighted on the map, honoring the room-level LH
 * `showOnKioskAndWebApp === false` flag. This is the SAME room-level gate that
 * suppresses the details popup — a room hidden on kiosk must also NOT be
 * highlighted on the map.
 *
 * Only an explicit room/desk-level `false` de-highlights (matching the popup's
 * room-level behavior); building/floor levels have their own separate effects
 * (error screen / removed from floor selector) and never reach a rendered
 * floor, so they are not folded in here.
 *
 * The set of hidden richMapIds on the floor is precomputed so each per-space
 * check is O(1), keeping the highlight hot path scan-free.
 *
 * Returns `(richMapId) => boolean`:
 *   - richMapId explicitly hidden on kiosk -> false (do not highlight)
 *   - otherwise                            -> true (today's behavior)
 */
export const createHiddenOnKioskPredicateForFloor = (location, floorNumber) => {
    const floorNode = findFloorNode(location, floorNumber);
    const hiddenRichMapIds = new Set();
    if (floorNode && typeof floorNode === 'object') {
        const node = floorNode;
        const buckets = [node.rooms, node.desks];
        for (const bucket of buckets) {
            if (!Array.isArray(bucket))
                continue;
            for (const entity of bucket) {
                if (isHiddenOnKiosk(entity)) {
                    const id = entity.richMapId;
                    if (typeof id === 'string')
                        hiddenRichMapIds.add(id);
                }
            }
        }
    }
    if (hiddenRichMapIds.size === 0)
        return () => true;
    return (richMapId) => !hiddenRichMapIds.has(richMapId);
};
